import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Env from '../../../shared/config/envConfig'
import { setMessage, setUser } from '../../../store/settings/settings.actions'
import { useHistory } from 'react-router-dom'
import TextInputComponent from '../../../shared/components/TextInputComponent'
import ButtonComponent from '../../../shared/components/ButtonComponent'
import { Severity } from '../../../shared/types'

const LoginComponent = () => {
  const history = useHistory()
  const dispatch = useDispatch()

  const theme: Theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))

  const [login, setLogin] = useState<string>('')
  const [password, setPassword] = useState<string>('')

  const imageOrientation = 1209 / 818
  const [screenOrientation, setScreenOrientation] = useState<number>(window.innerWidth / window.innerHeight)

  useEffect(() => {
    setScreenOrientation(window.innerWidth / window.innerHeight)
  }, [])

  const _onLogin = () => {
    const loginRequest = {
      login,
      password,
    }
    const basePath = Env.restBase
    const restUrl = `${basePath}/api/session/login`

    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')
    fetch(restUrl,
      {
        method: 'POST',
        body: JSON.stringify(loginRequest),
        credentials: 'include',
        headers: requestHeaders,
      })
      .then(res => res.json())
      .then(() => {
        const userUrl = `${basePath}/api/session/user`
        fetch(userUrl,
          {
            method: 'GET',
            credentials: 'include',
            headers: requestHeaders,
          })
          .then(res => res.json())
          .then(json => {
            !json.error && dispatch(setUser(json))
            history.push('/family')
          })
          .catch(err => console.log(err))
      })
      .catch(err => console.log(err))
  }

  const _onKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      _onLogin()
    }
  }

  const [showPassword, setShowPassword] = React.useState(false)
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = React.useState(false)
  const [username, setUsername] = React.useState<string | undefined>()

  const handleClickShowPassword = () => setShowPassword((show) => !show)

  return (
    <Box sx={{
      background: 'linear-gradient(rgba(0, 0, 0, .8), rgba(0, 0, 0, .5)), url("/images/ottoMeissner.jpg")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: imageOrientation > screenOrientation ? 'auto 100%' : '100% auto',
      height: '100vh',
    }}
    >
      <Box sx={{
        width: downSm ? '300px' : '500px',
        padding: '40px',
        backgroundColor: '#fff',
        opacity: 0.7,
        borderRadius: '10px',
        position: 'absolute',
        top: 'calc(50% - 50px)',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
      >
        <Typography variant='h1' sx={{ textAlign: 'center', width: '100%' }}>Willkommen</Typography>
        <form noValidate>
          <TextField
            variant='outlined'
            required
            fullWidth
            id='login'
            label='Login'
            autoComplete='login'
            value={login}
            onChange={(event) => setLogin(event.currentTarget.value)}
            onKeyPress={_onKeyPress}
          />
          <TextField
            required
            variant='outlined'
            fullWidth
            label='Password'
            type={showPassword ? 'text' : 'password'}
            id='password'
            autoComplete='current-password'
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
            onKeyPress={_onKeyPress}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={handleClickShowPassword}
                    edge='end'
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ margin: '10px 0 20px 0', width: '100%', textAlign: 'center', cursor: 'pointer' }}>
            <Link onClick={() => setShowForgotPasswordDialog(true)}>Passwort vergessen?</Link>
          </Box>
          <Button
            fullWidth
            sx={{ height: '50px' }}
            variant='contained'
            color='primary'
            onClick={_onLogin}
          >
            LOGIN
          </Button>
        </form>
      </Box>
      <Dialog
        open={showForgotPasswordDialog} onClose={() => {
          setUsername(undefined)
          setShowForgotPasswordDialog(false)
        }}
        sx={{ '& .MuiDialog-paper': { borderRadius: 0, minWidth: '300px', maxWidth: '600px' } }}
      >
        <Stack sx={{ p: 3 }}>
          <Stack
            direction='row' spacing={3} alignItems='center'
            justifyContent='space-between'
          >
            <DialogTitle sx={{ fontWeight: 600, p: 0, fontSize: '16px', marginBottom: '20px' }}>
              Passswort vergessen?
            </DialogTitle>
          </Stack>
          <DialogContent sx={{ p: 0, overflow: 'visible' }}>
            <Typography variant='body1'>Wenn du dein Passwort vergessen hast, kannst du es hier zurücksetzen. Gib bitte
              deine registrierte E-Mail Adresse oder deinen Nutzernamen ein und drücke "Passwort zurücksetzen".
              Danach wird dir eine E-Mail mit einem Link zugesandt. Mit diesem Link kannst du ein neues Passwort
              vergeben.
            </Typography>
            <TextInputComponent
              label='Nutzername oder E-Mail'
              fullWidth
              onChange={(value) => setUsername(value)}
            />
          </DialogContent>
        </Stack>
        <DialogActions sx={{ p: 3 }}>
          <ButtonComponent
            text='Abbrechen'
            variant='text'
            color='secondary'
            onClick={() => {
              setUsername(undefined)
              setShowForgotPasswordDialog(false)
            }}
          />
          <ButtonComponent
            text='Passwort zurücksetzen'
            color='primary'
            disabled={!username}
            variant='text'
            onClick={() => {
              const fetchHeaders = Env.fetchHeaders

              const basePath = Env.restBase
              const restUrl = `${basePath}/api/session/forgotPassword`
              fetch(restUrl,
                {
                  method: 'POST',
                  credentials: 'include',
                  headers: fetchHeaders,
                  body: JSON.stringify({
                    user: username,
                  }),
                })
                .then(res => {
                  setMessage({ message: 'E-Mails erfolgreich versandt', severity: Severity.success })
                  setShowForgotPasswordDialog(false)
                  setUsername(undefined)
                })
                .catch(() => {
                  setShowForgotPasswordDialog(false)
                  setUsername(undefined)
                  setMessage({
                    message: 'Ein unerwarteter Fehler. Bitte erneut versuchen.',
                    severity: Severity.error,
                  })
                })
            }}
          />
        </DialogActions>
      </Dialog>
    </Box>
  )
}
export default LoginComponent
